import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PublicHeader } from "../components/cod/PublicHeader";
import { Seo } from "../components/cod/Seo";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { publicOicApi } from "../lib/api";
import { Search, Calendar, DollarSign, Building2, ChevronRight, ArrowLeft, ExternalLink } from "lucide-react";

const CATEGORIES = [
  "Grant", "Procurement", "Employer Expansion", "Hiring", "Business Development",
  "Workforce", "Housing", "Healthcare", "Education", "Transportation",
  "Public Benefits", "Community Event", "Economic Development", "Small Business", "Legislation",
];
const AUDIENCES = [
  "Job Seekers", "Employers", "Investors", "Developers", "Businesses",
  "Nonprofits", "Community Organizations", "Students", "Veterans",
  "Returning Citizens", "Healthcare Workers",
];

// ═══════════════════════════════════════════════════════════════════════════
// Public feed  /opportunities
// ═══════════════════════════════════════════════════════════════════════════
export function OpportunityCenter() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [audience, setAudience] = useState("all");
  const [offset, setOffset] = useState(0);
  const PER_PAGE = 20;

  const load = () => {
    const params = { limit: PER_PAGE, offset };
    if (category !== "all") params.category = category;
    if (audience !== "all") params.audience = audience;
    if (q.trim()) params.search = q.trim();
    publicOicApi.list(params).then(r => { setItems(r.items); setTotal(r.total); }).catch(() => { setItems([]); setTotal(0); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, audience, offset]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Seo
        title="Opportunity Center — Chicago Opportunity Desk"
        description="Curated grants, contracts, jobs, and community opportunities benefiting Chicago employers, workers, investors, and residents. Updated continuously by the Chicago Opportunity Desk."
        path="/opportunities"
        schema="ProfessionalService"
      />
      <PublicHeader/>
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="text-xs uppercase tracking-widest text-[var(--cod-red)] font-semibold">Chicago Opportunity Desk</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 mt-2">Opportunity Center</h1>
          <div className="cod-rule-red mt-4"/>
          <p className="text-slate-600 mt-4 max-w-2xl">Grants, contracts, hiring surges, workforce programs, and community resources — curated for Chicago employers, workers, investors, and neighborhoods. Every item includes deadlines, funding amounts, and why it matters for our city.</p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid sm:grid-cols-3 gap-3 mb-6">
          <div className="relative sm:col-span-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input placeholder="Search opportunities…" value={q} onChange={e => setQ(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { setOffset(0); load(); } }} className="pl-9" data-testid="oc-search"/>
          </div>
          <Select value={category} onValueChange={v => { setCategory(v); setOffset(0); }}>
            <SelectTrigger data-testid="oc-category"><SelectValue placeholder="All categories"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={audience} onValueChange={v => { setAudience(v); setOffset(0); }}>
            <SelectTrigger data-testid="oc-audience"><SelectValue placeholder="All audiences"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All audiences</SelectItem>
              {AUDIENCES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="text-xs text-slate-500 mb-3">
          {total > 0 ? `${total} opportunities` : "No published opportunities yet — check back soon."}
        </div>

        <div className="space-y-3" data-testid="oc-list">
          {items.map(i => (
            <Link to={`/opportunities/${i.slug}`} key={i.id} data-testid={`oc-item-${i.id}`}
              className="block rounded-lg border border-slate-200 bg-white p-5 hover:border-[var(--cod-red)] transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-slate-100 text-slate-700">{i.opportunity_type}</span>
                    {i.urgency_level === "red" && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-red-100 text-red-800">Deadline soon</span>}
                    {i.audiences?.slice(0, 3).map((a, idx) => (
                      <span key={idx} className="text-[10px] text-slate-500">· {a}</span>
                    ))}
                  </div>
                  <h2 className="font-serif text-xl text-slate-900 leading-snug">{i.article_headline}</h2>
                  <p className="text-sm text-slate-600 mt-2 line-clamp-2">{i.seo_meta_description}</p>
                  <div className="mt-3 flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                    {i.agency && <span className="flex items-center gap-1"><Building2 size={12}/>{i.agency}</span>}
                    {i.deadline_at && <span className="flex items-center gap-1"><Calendar size={12}/>{String(i.deadline_at).slice(0, 10)}</span>}
                    {i.funding_amount && <span className="flex items-center gap-1"><DollarSign size={12}/>{i.funding_amount}</span>}
                    {i.published_at && <span>Posted {String(i.published_at).slice(0, 10)}</span>}
                  </div>
                </div>
                <ChevronRight size={20} className="text-slate-400 flex-shrink-0 mt-1"/>
              </div>
            </Link>
          ))}
        </div>

        {total > PER_PAGE && (
          <div className="mt-6 flex justify-between items-center">
            <Button variant="outline" onClick={() => setOffset(Math.max(0, offset - PER_PAGE))} disabled={offset === 0}>Previous</Button>
            <span className="text-xs text-slate-500">Showing {offset + 1}–{Math.min(offset + PER_PAGE, total)} of {total}</span>
            <Button variant="outline" onClick={() => setOffset(offset + PER_PAGE)} disabled={offset + PER_PAGE >= total}>Next</Button>
          </div>
        )}
      </main>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Public article  /opportunities/{slug}
// ═══════════════════════════════════════════════════════════════════════════
export function OpportunityArticle() {
  const { slug } = useParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    publicOicApi.detail(slug).then(setO).catch(() => setErr("Not found"));
  }, [slug]);

  const html = useMemo(() => {
    if (!o?.article_body_md) return "";
    // Simple Markdown → HTML: headings, paragraphs, lists
    let s = o.article_body_md
      .replace(/\r\n/g, "\n")
      .replace(/^### (.+)$/gm, "<h3>$1</h3>")
      .replace(/^## (.+)$/gm, "<h2>$1</h2>")
      .replace(/^# (.+)$/gm, "<h2>$1</h2>")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/^- (.+)$/gm, "<li>$1</li>")
      .replace(/(<li>.*<\/li>\n?)+/g, m => `<ul>${m}</ul>`)
      .split(/\n\n+/).map(para => para.startsWith("<") ? para : `<p>${para}</p>`).join("\n");
    return s;
  }, [o]);

  if (err) return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader/>
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <h1 className="font-serif text-3xl text-slate-900">Opportunity not found</h1>
        <p className="text-slate-600 mt-3">This opportunity may have expired or been unpublished.</p>
        <Link to="/opportunities" className="cod-link mt-4 inline-block">← Back to Opportunity Center</Link>
      </div>
    </div>
  );
  if (!o) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Seo
        title={`${o.article_headline} — Chicago Opportunity Desk`}
        description={o.seo_meta_description}
        path={`/opportunities/${slug}`}
        schema="NewsArticle"
      />
      <PublicHeader/>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/opportunities" className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-4"><ArrowLeft size={12}/> Opportunity Center</Link>
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-slate-100 text-slate-700">{o.opportunity_type}</span>
          {o.urgency_level === "red" && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-red-100 text-red-800">Deadline soon</span>}
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl text-slate-900" data-testid="oa-title">{o.article_headline}</h1>
        <div className="cod-rule-red mt-4"/>
        <div className="text-xs text-slate-500 mt-4 flex items-center gap-4 flex-wrap">
          {o.agency && <span className="flex items-center gap-1"><Building2 size={12}/>{o.agency}</span>}
          {o.deadline_at && <span className="flex items-center gap-1"><Calendar size={12}/>Deadline: {String(o.deadline_at).slice(0, 10)}</span>}
          {o.funding_amount && <span className="flex items-center gap-1"><DollarSign size={12}/>{o.funding_amount}</span>}
          {o.published_at && <span>Published {String(o.published_at).slice(0, 10)}</span>}
        </div>

        <div className="prose prose-slate max-w-none mt-6 leading-relaxed text-slate-800"
          data-testid="oa-body"
          dangerouslySetInnerHTML={{ __html: html }}/>

        <Card className="mt-8 border-[var(--cod-red)] border-l-4 bg-white">
          <CardContent className="pt-6">
            <div className="text-xs uppercase tracking-widest text-[var(--cod-red)] font-semibold">Need help getting started?</div>
            <div className="text-slate-800 mt-2">Chicago Opportunity Desk connects Chicago residents, employers, and organizations with opportunities like this. Set up a free account and our team will help you take the next step.</div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/onboarding/employer"><Button className="cod-bg-red">I&#39;m an Employer</Button></Link>
              <Link to="/onboarding/partner"><Button variant="outline">I&#39;m a Partner Organization</Button></Link>
              <Link to="/start"><Button variant="outline">I&#39;m looking for work or training</Button></Link>
            </div>
            <div className="text-xs text-slate-500 mt-4">
              📞 (312) 343-4183 · ✉️ <a href="mailto:info@joinCOD.org" className="cod-link">info@joinCOD.org</a>
            </div>
          </CardContent>
        </Card>

        {o.raw_url && (
          <div className="mt-6 text-xs text-slate-500">
            <a href={o.raw_url} target="_blank" rel="noopener noreferrer" className="cod-link inline-flex items-center gap-1">
              View original source <ExternalLink size={10}/>
            </a>
          </div>
        )}
      </article>
    </div>
  );
}
